// ============================================================
// src/components/MapPicker.js
//
// ── WHAT THIS FILE DOES ──────────────────────────────────────
// An interactive OpenStreetMap powered by Leaflet that lets
// the user set their home location by:
//   a) dragging the pin anywhere on the map, or
//   b) clicking anywhere on the map to move the pin there
//
// When the pin moves, we reverse-geocode with Nominatim to get
// a readable address and call onLocationChange so the parent
// (settings.js) can update its state.
//
// ── INSTALL ──────────────────────────────────────────────────
//   npm install leaflet
//
// Also add to your public/index.html <head>:
//   <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
//
// ── PROPS ────────────────────────────────────────────────────
//   lat             number | null   — current marker latitude
//   lng             number | null   — current marker longitude
//   onLocationChange  function      — called with { lat, lng, address }
//                                    whenever the pin moves
// ============================================================

import { useEffect, useRef, useCallback } from 'react'


// ── CONSTANTS ─────────────────────────────────────────────────

// Default map center when no lat/lng is provided yet.
// Charlotte, NC — Ben's city.
const DEFAULT_LAT = 35.2271
const DEFAULT_LNG = -80.8431
const DEFAULT_ZOOM = 13

// Nominatim reverse geocode endpoint
const NOMINATIM_REVERSE = 'https://nominatim.openstreetmap.org/reverse'
const NOMINATIM_USER_AGENT = 'DragPocketPlanner/1.0 (https://drag-helper.netlify.app)'

// OSM tile layer
const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'


// ── REVERSE GEOCODE HELPER ────────────────────────────────────
// Takes lat/lng and returns a human-readable address string.
// Returns a coordinate string as fallback if Nominatim fails.
async function reverseGeocode(lat, lng) {
  try {
    const params = new URLSearchParams({
      lat:    String(lat),
      lon:    String(lng),
      format: 'json',
      zoom:   '17',
      // zoom=17 = street-level detail in the returned address
    })

    const response = await fetch(`${NOMINATIM_REVERSE}?${params}`, {
      headers: { 'User-Agent': NOMINATIM_USER_AGENT },
    })

    if (!response.ok) throw new Error(`Nominatim ${response.status}`)

    const data = await response.json()
    return data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`

  } catch {
    // network failure or Nominatim rate limit — return coords as fallback
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
  }
}


// ── CREATE CUSTOM PIN ICON ─────────────────────────────────────
// Returns a Leaflet DivIcon using the DPP hot pink brand color.
// We use a CSS-drawn pin shape instead of an image file so there
// are no missing asset issues.
function createHomeIcon(L) {
  return L.divIcon({
    className: '',
    // className='' prevents Leaflet from adding its own background/border
    html: `
      <div style="
        position: relative;
        width: 28px;
        height: 36px;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.35));
      ">
        <!-- pin body -->
        <div style="
          position: absolute;
          top: 0; left: 0;
          width: 28px; height: 28px;
          background: #FF2D87;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 2.5px solid #fff;
        "></div>
        <!-- house icon inside pin -->
        <div style="
          position: absolute;
          top: 4px; left: 4px;
          width: 20px; height: 20px;
          display: flex; align-items: center; justify-content: center;
          color: #fff;
          font-size: 11px;
          transform: rotate(45deg);
        ">
          <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='white' viewBox='0 0 16 16'>
            <path d='M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L8 2.207l6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z'/>
            <path d='M13 7.293l-5-5-5 5V14a1 1 0 0 0 1 1h3v-3h2v3h3a1 1 0 0 0 1-1z'/>
          </svg>
        </div>
        <!-- pin tail shadow dot -->
        <div style="
          position: absolute;
          bottom: 0; left: 50%;
          transform: translateX(-50%);
          width: 6px; height: 6px;
          background: rgba(0,0,0,0.2);
          border-radius: 50%;
        "></div>
      </div>
    `,
    iconSize:   [28, 36],
    iconAnchor: [14, 36],
    // iconAnchor = [half width, full height] = pin tip touches the coordinate
    popupAnchor: [0, -36],
  })
}


// ============================================================
// MapPicker COMPONENT
// ============================================================
export default function MapPicker({ lat, lng, onLocationChange }) {

  // Refs — stable across renders, don't trigger re-renders when mutated
  const containerRef  = useRef(null)  // the div the map attaches to
  const mapRef        = useRef(null)  // the Leaflet map instance
  const markerRef     = useRef(null)  // the Leaflet marker instance
  //eslint-disable-next-line no-unused-vars
  const isMovingRef   = useRef(false) // debounce: skip reverse geocode while dragging


  // ── onPinMoved ─────────────────────────────────────────────
  // Called whenever the pin settles at a new position.
  // Reverse geocodes and calls onLocationChange with the result.
  const onPinMoved = useCallback(async (newLat, newLng) => {
    // immediately call back with coordinates so the parent UI is responsive
    // even before we have a readable address
    onLocationChange({ lat: newLat, lng: newLng, address: null })

    const address = await reverseGeocode(newLat, newLng)
    onLocationChange({ lat: newLat, lng: newLng, address })
  }, [onLocationChange])


  // ── INITIALIZE MAP ─────────────────────────────────────────
  // Runs once after mount to create the Leaflet map.
  // Dynamic import of leaflet avoids SSR issues and keeps
  // the initial JS bundle smaller.
  useEffect(() => {
    if (!containerRef.current) return

    // dynamic import — leaflet must be in node_modules
    import('leaflet').then((L) => {

      // prevent double-init (React StrictMode runs effects twice in dev)
      if (mapRef.current) return

      // ── create map ────────────────────────────────────────
      const map = L.map(containerRef.current, {
        center:    [lat ?? DEFAULT_LAT, lng ?? DEFAULT_LNG],
        zoom:      DEFAULT_ZOOM,
        zoomControl: true,
        // attributionControl stays on for OSM license compliance
      })

      // ── add OSM tile layer ────────────────────────────────
      L.tileLayer(OSM_TILE_URL, {
        attribution: OSM_ATTRIBUTION,
        maxZoom: 19,
      }).addTo(map)

      // ── add draggable marker ──────────────────────────────
      const marker = L.marker(
        [lat ?? DEFAULT_LAT, lng ?? DEFAULT_LNG],
        {
          draggable: true,
          icon:      createHomeIcon(L),
          title:     'Drag to set your home location',
          // title shows as browser tooltip on hover
        }
      ).addTo(map)

      marker.bindTooltip('Drag me to your home', {
        permanent:  false,
        direction:  'top',
        offset:     [0, -36],
        className:  'dpp-map-tooltip',
      })

      // ── marker drag end: update location ─────────────────
      marker.on('dragend', (e) => {
        const pos = e.target.getLatLng()
        onPinMoved(pos.lat, pos.lng)
      })

      // ── map click: move marker + update location ──────────
      map.on('click', (e) => {
        marker.setLatLng(e.latlng)
        onPinMoved(e.latlng.lat, e.latlng.lng)
      })

      // store refs so other effects can access the instances
      mapRef.current    = map
      markerRef.current = marker

    }).catch((err) => {
      console.error('MapPicker: failed to load Leaflet:', err)
    })

    // ── cleanup on unmount ────────────────────────────────
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current    = null
        markerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // [] = init once; lat/lng changes handled by the effect below


  // ── SYNC MARKER POSITION when lat/lng props change ─────────
  // This fires when the user types an address and clicks
  // "Find Address" — the geocoded lat/lng flows back here
  // so the map moves the pin without a full re-render.
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return
    if (lat == null || lng == null) return

    const newLatLng = [lat, lng]
    markerRef.current.setLatLng(newLatLng)
    mapRef.current.setView(newLatLng, Math.max(mapRef.current.getZoom(), 14), {
      animate: true,
      duration: 0.6,
      // smooth pan + zoom to the new location
    })
  }, [lat, lng])


  // ── RENDER ─────────────────────────────────────────────────
  return (
    <div style={{ position: 'relative' }}>

      {/* the map container — Leaflet attaches to this div */}
      <div
        ref={containerRef}
        style={{
          height:       '320px',
          width:        '100%',
          borderRadius: '12px',
          overflow:     'hidden',
          border:       '2px solid #dee2e6',
          // border gives the map a clean edge against the card background
        }}
      />

      {/* OSM attribution note (required by OSM license, Leaflet also adds its own) */}
      {/* Leaflet's built-in attribution handles the legal requirement */}

      {/* hint label below the map */}
      <div
        className="text-muted small mt-1"
        style={{ userSelect: 'none' }}
      >
        <i className="bi bi-cursor me-1"></i>
        Click anywhere on the map or drag the pin to adjust your location.
      </div>

      {/* tooltip style injected inline — avoids needing a separate CSS file */}
      <style>{`
        .dpp-map-tooltip {
          background: #1a1a2e;
          color: #fff;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          padding: 4px 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .dpp-map-tooltip::before {
          border-top-color: #1a1a2e !important;
        }
      `}</style>

    </div>
  )
}
